import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/Button";
import { toFetchableUrl } from "../../../utils/imageFetch";

const HANDLE_RADIUS = 30;

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export default function LogoEditor({
  backgroundFile,
  backgroundUrl,
  initialLogoFile,
  onApply,
  onClose,
  onChangeLogo,
  showTools = false,
  onToolsChange,
}) {
  const { t } = useTranslation();

  const [canvasSize, setCanvasSize] = useState({ w: 1080, h: 1080 });
  const [logoBitmap, setLogoBitmap] = useState(null);
  const [pos, setPos] = useState({ x: 60, y: 60 });
  const [logoWidth, setLogoWidth] = useState(200);
  const [opacity, setOpacity] = useState(100);
  const [applying, setApplying] = useState(false);

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const isFirstLogoRender = useRef(true);

  const logoHeight = logoBitmap
    ? logoWidth / (logoBitmap.width / logoBitmap.height)
    : 0;

  const handleReset = () => {
    setLogoWidth(Math.round(canvasSize.w * 0.18));
    setPos({ x: Math.round(canvasSize.w * 0.05), y: Math.round(canvasSize.h * 0.05) });
    setOpacity(100);
  };

  // Draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvasSize.w, canvasSize.h);
    // No background — img tag handles it

    if (logoBitmap) {
      ctx.save();
      ctx.globalAlpha = opacity / 100;
      ctx.drawImage(logoBitmap, pos.x, pos.y, logoWidth, logoHeight);
      ctx.restore();

      const hx = pos.x + logoWidth;
      const hy = pos.y + logoHeight;
      ctx.beginPath();
      ctx.arc(hx, hy, 18, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#111827";
      ctx.stroke();
    }
  }, [logoBitmap, pos, logoWidth, logoHeight, opacity, canvasSize]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    if (!initialLogoFile) return;
    createImageBitmap(initialLogoFile).then((bitmap) => {
      setLogoBitmap(bitmap);
      setLogoWidth(Math.round(canvasSize.w * 0.18));
      setPos({ x: Math.round(canvasSize.w * 0.05), y: Math.round(canvasSize.h * 0.05) });
    }).catch(console.error);
  }, [initialLogoFile, canvasSize.w, canvasSize.h]);

  // Open the tools as soon as a logo lands, but not on mount.
  useEffect(() => {
    if (isFirstLogoRender.current) {
      isFirstLogoRender.current = false;
      return;
    }
    if (logoBitmap) onToolsChange?.(true);
  }, [logoBitmap]);

  // Pointer events
  const toCanvasCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvasSize.w,
      y: ((e.clientY - rect.top) / rect.height) * canvasSize.h,
    };
  };

  const onPointerDown = (e) => {
    if (!logoBitmap) return;
    const { x: cx, y: cy } = toCanvasCoords(e);
    const hx = pos.x + logoWidth;
    const hy = pos.y + logoHeight;
    const nearHandle = Math.hypot(cx - hx, cy - hy) <= HANDLE_RADIUS;
    const inBody = cx >= pos.x && cx <= hx && cy >= pos.y && cy <= hy;
    if (!nearHandle && !inBody) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    dragRef.current = {
      mode: nearHandle ? "resize" : "move",
      startX: cx, startY: cy,
      origX: pos.x, origY: pos.y,
      origW: logoWidth,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const { x: cx, y: cy } = toCanvasCoords(e);
    if (d.mode === "move") {
      setPos({
        x: clamp(d.origX + (cx - d.startX), 0, canvasSize.w - logoWidth),
        y: clamp(d.origY + (cy - d.startY), 0, canvasSize.h - logoHeight),
      });
    } else {
      setLogoWidth(clamp(d.origW + (cx - d.startX), 40, canvasSize.w - pos.x));
    }
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  };

  // Apply — composite background + logo into one blob
  const handleApply = async () => {
    if (!logoBitmap) return;
    setApplying(true);
    try {
      const composite = document.createElement("canvas");
      composite.width = canvasSize.w;
      composite.height = canvasSize.h;
      const ctx = composite.getContext("2d");

      // 1. Draw background
      let bgBitmap = null;
      if (backgroundFile) {
        bgBitmap = await createImageBitmap(backgroundFile);
      } else if (backgroundUrl) {
        try {
          const res = await fetch(toFetchableUrl(backgroundUrl));
          const blob = await res.blob();
          bgBitmap = await createImageBitmap(blob);
        } catch {
          const tempImg = new Image();
          tempImg.crossOrigin = "anonymous";
          const imgLoaded = await new Promise((res) => {
            tempImg.onload = () => res(true);
            tempImg.onerror = () => res(false);
            tempImg.src = backgroundUrl;
          });
          if (imgLoaded) {
            ctx.drawImage(tempImg, 0, 0, canvasSize.w, canvasSize.h);
          }
          // if not loaded (CORS blocked on localhost), continues with logo-only
        }
      }
      if (bgBitmap) {
        ctx.drawImage(bgBitmap, 0, 0, canvasSize.w, canvasSize.h);
      }

      // 2. Draw logo on top
      ctx.save();
      ctx.globalAlpha = opacity / 100;
      ctx.drawImage(logoBitmap, pos.x, pos.y, logoWidth, logoHeight);
      ctx.restore();

      // 3. Export composite
      composite.toBlob((blob) => {
        setApplying(false);
        if (blob) onApply?.(blob);
      }, "image/png");

    } catch (err) {
      console.error("LogoEditor: export failed (CORS taint?):", err);
      setApplying(false);
    }
  };

  return (
    <div className="w-full flex flex-col">

      {/* Tools bar — sits between ImageSection's toolbar and the image */}
      {showTools && logoBitmap && (
        <div className="bg-gray-200 border-x border-gray-200 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Size */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-black-600">{t("logoEditor.size")}</span>
                <span className="text-xs font-bold text-black-800">{Math.round(logoWidth)}px</span>
              </div>
              <input
                type="range"
                min={40}
                max={Math.round(canvasSize.w * 0.8)}
                step={1}
                value={logoWidth}
                onChange={(e) => setLogoWidth(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
            </div>

            {/* Opacity */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-black-600">{t("logoEditor.opacity")}</span>
                <span className="text-xs font-bold text-black-800">{opacity}%</span>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={opacity}
                onChange={(e) => setOpacity(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-200">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="text-xs font-bold underline text-black-800 hover:text-black"
              >
                {t("logoEditor.reset")}
              </button>
              <button
                type="button"
                onClick={() => { onToolsChange?.(false); onChangeLogo?.(); }}
                className="text-xs font-bold underline text-black-800 hover:text-black"
              >
                {t("logoEditor.changeLogo")}
              </button>
            </div>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} className="!text-sm !py-1.5 !px-4">
                {t("buttons.cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleApply}
                disabled={!logoBitmap || applying}
                className="!text-sm !py-1.5 !px-4"
              >
                {applying ? t("logoEditor.applying") : t("logoEditor.apply")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="relative w-full rounded-bl-lg rounded-br-lg overflow-hidden border border-black-40">
        {/* Background shown via img tag — no CORS restriction for display */}
        <img
          src={backgroundUrl}
          alt="background"
          className="block w-full h-auto"
          onLoad={(e) => {
            const img = e.target;
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            setCanvasSize({ w, h });
            setPos({ x: Math.round(w * 0.05), y: Math.round(h * 0.05) });
            setLogoWidth(Math.round(w * 0.18));
          }}
        />
        {/* Canvas overlaid — transparent bg, only draws logo + handle */}
        <canvas
          ref={canvasRef}
          width={canvasSize.w}
          height={canvasSize.h}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="absolute inset-0 w-full h-full"
          style={{ touchAction: "none", cursor: logoBitmap ? "grab" : "default" }}
        />
      </div>
    </div>
  );
}